import { SelfContainedCommand, CommandExecutionContext } from '../../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../../types/standardToolResults';
import { createFileLogger } from '../../../../utils/logger';

const log = createFileLogger('NEW_WINDOW_CREATE');
const NEW_WINDOW_PERF_LOGS = import.meta.env.DEV;

function perfLog(message: string, data?: Record<string, unknown>) {
  if (!NEW_WINDOW_PERF_LOGS) return;
  console.info(`[NewWindowPerf] ${message}`, data ?? {});
}

export const newWindowCreateCommand: SelfContainedCommand = {
  id: 'new_window_create',
  description: '✨ CREATE: Create a new Window entity in launcher state',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'title',
        type: 'string',
        description: 'Title for the window',
        required: false,
        defaultValue: 'New Window'
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space ID',
        required: false
      },
      {
        name: 'windowId',
        type: 'string',
        description: 'Window ID to replace (optional)',
        required: false
      },
      {
        name: 'createWindow',
        type: 'boolean',
        description: 'Whether to create a new window or replace existing',
        required: false,
        defaultValue: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { title: 'Start' },
        expectedOutput: createSuccessResult('Window created', {
          entityId: 'entity_123',
          title: 'Start'
        }),
        description: 'Create a new launcher window'
      }
    ],
    bad: []
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const perfStartedAt = performance.now();
    const { title = 'New Window', spaceId: providedSpaceId, windowId, createWindow = false } = params;
    const spaceId = providedSpaceId || context.spaceId;
    
    try {
      // ✅ Capture previous active window for focus restoration
      const { useSpaceStore } = await import('../../../../stores/spaceStore');
      const previousActiveWindowId = useSpaceStore.getState().activeWindowId;
      
      log.log('[new_window_create] Creating Window entity:', { 
        title, 
        spaceId, 
        windowId, 
        previousActiveWindowId 
      });
      perfLog('command-started', { createWindow, replacingWindowId: windowId ?? null });
      
      // ✅ Use entityStore.actions for proper orchestration
      const useEntityStore = (await import('../../../../stores/entityStore')).default;
      const newEntity = await useEntityStore.getState().actions.createEntity({
        entityType: 'Window',
        name: title.trim(),
        spaceId: spaceId,
        skeleton: {
          '@type': 'Window',
          displayName: title.trim(),
          createdAt: new Date().toISOString(),
          lastAccessedAt: new Date().toISOString(),
          previousActiveWindowId  // Store for focus restoration on close
        }
      });
      
      log.log('[new_window_create] Window entity created:', { id: newEntity.id });
      perfLog('entity-created', {
        entityId: newEntity._id || newEntity.id,
        durationMs: Math.round(performance.now() - perfStartedAt),
      });
      
      const entityId = newEntity._id || newEntity.id;
      
      // Open the Window entity in a window (marked with isNew flag for launcher state)
      if (createWindow || !windowId) {
        log.log('[new_window_create] Opening Window entity in new window');
        log.log('[new_window_create] Creating window with previousActiveWindowId:', {
          previousActiveWindowId,
          willRestoreToThisOnClose: !!previousActiveWindowId
        });
        
        const createdWindowId = await useSpaceStore.getState().actions.createWindow({
          appType: 'new-window',
          entity: newEntity,
          position: { x: window.innerWidth / 2 - 400, y: window.innerHeight / 2 - 300 },
          size: { width: 800, height: 600 },
          applicationState: {
            isNew: true,  // Show launcher UI
            previousActiveWindowId: previousActiveWindowId || undefined  // Convert null to undefined
          }
        });
        
        log.log('[new_window_create] Window created, verifying applicationState:', {
          windowId: createdWindowId,
          applicationState: useSpaceStore.getState().windows[createdWindowId]?.applicationState
        });
        perfLog('window-created', {
          windowId: createdWindowId,
          durationMs: Math.round(performance.now() - perfStartedAt),
        });
        
        // Bring to front
        useSpaceStore.getState().bringWindowToFront(createdWindowId);
      } else {
        log.log('[new_window_create] Opening Window entity in existing window:', windowId);
        
        // Update existing window to show Window entity in launcher state
        useSpaceStore.getState().actions.updateWindow(windowId, {
          entityId,
          entityLlmId: newEntity.llmId,
          appType: 'new-window',
          title: 'New Window',
          applicationState: {
            isNew: true,  // Show launcher UI
            previousActiveWindowId: previousActiveWindowId || undefined  // Convert null to undefined
          }
        });
        perfLog('window-replaced', {
          windowId,
          durationMs: Math.round(performance.now() - perfStartedAt),
        });
      }
      
      return createSuccessResult(
        `Window created successfully`,
        {
          entityId: newEntity._id || newEntity.id,
          name: title.trim()
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime
          }
        }
      );
    } catch (error: any) {
      log.error('[new_window_create] Error:', error);
      return createErrorResult(
        'Failed to create Window',
        error.message || 'Unknown error',
        'execution'
      );
    }
  }
};

// Don't register in entity creation registry - Windows aren't user-creatable from sidebar
// They're created via the + button in WindowEntityTabs

